import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link } from "gatsby";
import { Container } from "../../components/styled/ProductsStyled";
import { AuthUserContext } from "../../context/AuthUser";
import { getImageSearchHistory } from "../../services/api";
import { markdownToHtml } from "../../utils/markdownToHtml";
import ImageSearchSuggestion from "../../components/cards/ImageSearchSuggestion";
import Loader from "../../components/Loader";

const ImageSearchHistory = ({ id }) => {
  const { authUser } = useContext(AuthUserContext);
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedHistory = useMemo(() => {
    if (!id) return null;
    return histories.find((h) => String(h.id) === String(id)) || null;
  }, [histories, id]);

  const selectedProducts = useMemo(() => {
    if (!selectedHistory) return [];
    return (selectedHistory.results || [])
      .map((r) => ({
        product: r.product,
        score: r.score,
        reason: r.reason,
        matchPoints: r.matchPoints || [],
      }))
      .filter((item) => item.product);
  }, [selectedHistory]);

  const feedbackByProductId = useMemo(() => {
    if (!selectedHistory) return {};
    const map = {};
    (selectedHistory.feedbacks || []).forEach((feedback) => {
      const productId = feedback.product?.id || feedback.product;
      if (productId) {
        map[productId] = feedback;
      }
    });
    return map;
  }, [selectedHistory]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    getImageSearchHistory(token)
      .then((res) => {
        if (res.data) {
          setHistories(res.data);
        } else {
          setError("Could not load search history.");
        }
      })
      .catch(() => setError("Could not load search history."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Container className="container-fluid">
        <p className="image-search-error">{error}</p>
      </Container>
    );
  }

  if (selectedHistory) {
    const analysisHtml = markdownToHtml(selectedHistory.aiAnalysis || "");

    return (
      <div className="image-search-history-page">
        <Container className="container-fluid">
          <div className="image-search-history-header">
            <Link to="/member/history" className="history-back-link">
              ← Back to history
            </Link>
            <h1>Search Result</h1>
          </div>

          <div className="image-search-history-detail">
            {selectedHistory.searchType === "text" ? (
              <div className="history-detail__text-badge">
                <span>Text search</span>
              </div>
            ) : (
              selectedHistory.imageThumbnail && (
                <div className="history-detail__image">
                  <img
                    src={selectedHistory.imageThumbnail}
                    alt="Uploaded fabric"
                  />
                </div>
              )
            )}

            <div className="history-detail__info">
              {selectedHistory.userComment && (
                <div className="history-detail__comment">
                  <strong>Your request:</strong>
                  <p>{selectedHistory.userComment}</p>
                </div>
              )}

              <p className="history-detail__date">
                {new Date(selectedHistory.createdAt).toLocaleString()}
              </p>
            </div>
          </div>

          {selectedHistory.aiAnalysis ? (
            <div className="image-search-analysis">
              <div
                className="image-search-analysis__content"
                dangerouslySetInnerHTML={{ __html: analysisHtml }}
              />
            </div>
          ) : (
            <p className="image-search-history-empty">
              No analysis available for this search.
            </p>
          )}

          <h2 className="history-detail__subtitle">Suggested fabrics</h2>

          {selectedProducts.length === 0 ? (
            <p>No products found for this search.</p>
          ) : (
            <div className="products-grid">
              {selectedProducts.map((item) => (
                <ImageSearchSuggestion
                  key={item.product.id}
                  product={item.product}
                  score={item.score}
                  matchReason={item.reason}
                  matchPoints={item.matchPoints}
                  historyId={selectedHistory.id}
                  token={token}
                  isLoggedIn={authUser?.logged}
                  existingFeedback={feedbackByProductId[item.product.id]}
                />
              ))}
            </div>
          )}
        </Container>
      </div>
    );
  }

  return (
    <div className="image-search-history-page">
      <Container className="container-fluid">
        <h1>AI Search History</h1>

        {histories.length === 0 ? (
          <p className="image-search-history-empty">
            You haven&apos;t made any image searches yet.
          </p>
        ) : (
          <div className="image-search-history-list">
            {histories.map((history) => (
              <div key={history.id} className="image-search-history-card">
                {history.imageThumbnail ? (
                  <div className="history-card__image">
                    <img
                      src={history.imageThumbnail}
                      alt="Uploaded fabric"
                    />
                  </div>
                ) : (
                  <div className="history-card__image history-card__image--text">
                    <span>Text search</span>
                  </div>
                )}

                <div className="history-card__content">
                  <p className="history-card__date">
                    {new Date(history.createdAt).toLocaleString()}
                  </p>

                  {history.userComment && (
                    <p className="history-card__comment">
                      {history.userComment}
                    </p>
                  )}

                  <Link
                    to={`/member/history/${history.id}`}
                    className="history-card__link"
                  >
                    View results
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};

export default ImageSearchHistory;
